import React, { useState, useEffect } from "react";
import styles from "./ReportDetails.module.css";
import ReportHeader from "../ReportHeader";
import SelectDropDown from "../SelectDropdown";
import ReportItem from "../ReportItem";
import ReportInfoItem from "../ReportInfoItem";
import ReportList from "../ReportList";
import DateHeader from "../DateHeader";
import Loader from "components/Loader";
import CheckedInList from "components/CheckedInList";
import EventHeader from "components/EventHeader";

import Search from "./icons/Search.png";
import { SearchModal } from "../SearchModal/SearchModal";

import { useMajorEventReportGet } from "./useMajorEventReportGet";
import { useReportsGet } from "./useReportsGet";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";

export const ReportDetails = ({
  reportType,
  onBack,
  ibo,
  header,
  changeIbo,
  previousIbo,
  onIboBack,
  setCurrentIboName,
  initialContext,
}) => {
  const {t} = useTranslation();
  const history = useHistory();
  const currentDate = new Date();
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const [iboNum, setIboNum] = useState(initialContext?.ibo || ibo);
  const [year, setYear] = useState(
    initialContext?.year || String(currentDate.getFullYear())
  );
  const [month, setMonth] = useState(
    initialContext?.month || monthNames[currentDate.getMonth()]
  );
  const [attendees, setAttendeees] = useState(
    initialContext?.attendees || "Total"
  );
  const [location, setLocation] = useState(
    initialContext?.location || "All"
  );
  const [type, setType] = useState(initialContext?.type || "All");
  const [status, setStatus] = useState(initialContext?.status || "All");
  const [showCheckedIn, setShowCheckedIn] = useState(false);
  const { data, isLoading, refetch } = useReportsGet({
    reportType,
    ibo,
    year,
    month: monthNames.indexOf(month),
    // Major Events
    event: header,
    attendees: attendees === "Total" ? "all" : attendees,
    location: location.toLowerCase(),
    // Membership
    type: type.toLowerCase(),
    status: status.toLowerCase(),
  });

  const Attendees = [
    "Total",
    "Paid",
    "Founders Platinum/VIP",
    "Complimentary",
    "Non Purchased",
    "Virtual",
    "Never Checked In",
  ];
  const Types = [
    "All",
    "Premier + Digital",
    "Premier",
    "Basic",
    "Complimentary",
  ];
  const Locations = [
    "All",
    "St. Paul",
    "Colorado Springs",
    "Honolulu",
    "Boise",
    "Las Vegas",
    "Red Deer",
    "Ft. Lauderdale",
  ];
  const Statuses = ["All", "Active", "Inactive", "Canceled", "Discontinued"];

  const [reportData, setReportData] = useState(null);

  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (data) {
      setReportData(data);

      if (data.downline.length === 0) {
        setShowCheckedIn(true);
      }

      if (data.iboInfo?.iboName && typeof setCurrentIboName === "function") {
        setCurrentIboName(data.iboInfo.iboName);
      }
    }
  }, [data]);

  useEffect(() => {
    if (initialContext) {
      if (initialContext.ibo) setIboNum(initialContext.ibo);
      if (initialContext.year) setYear(initialContext.year);
      if (initialContext.month) setMonth(initialContext.month);
      if (initialContext.attendees) setAttendeees(initialContext.attendees);
      if (initialContext.location) setLocation(initialContext.location);
      if (initialContext.type) setType(initialContext.type);
      if (initialContext.status) setStatus(initialContext.status);
    }
  }, []);

  if (isLoading || !reportData) {
    return (
      <div className={styles.loaderContainer}>
        <Loader color="#22252e" />
      </div>
    );
  }

  return (
    <>
      <div className={styles.container}>
        <div
          className={styles.back}
          onClick={() => {
            if (showCheckedIn) {
              setShowCheckedIn(false);
            } else if (previousIbo) {
              onIboBack();
            } else {
              onBack();
            }
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M15.9139 17.5L10.4139 12L15.9139 6.5L14.4999 5.086L7.58594 12L14.4999 18.914L15.9139 17.5Z"
              fill="#9E703C"
            />
          </svg>
          {showCheckedIn
            ? reportData.iboInfo.iboName
            : previousIbo
            ? previousIbo.iboName
            : t("Reports")}
        </div>

        <SearchModal isOpen={isOpen} setIsOpen={setIsOpen} />

        <div className={styles.years}>
          <DateHeader
            month={month}
            year={year}
            hideArrows={true}
            onMonthChange={(newMonth) => {
              setMonth(newMonth);
            }}
            onYearChange={(newYear) => {
              const currentDate = new Date();
              const currentYear = currentDate.getFullYear();
              const currentMonthIndex = currentDate.getMonth();
              const selectedMonthIndex = monthNames.findIndex(
                (m) => m === month
              );

              if (
                newYear === currentYear &&
                selectedMonthIndex > currentMonthIndex
              ) {
                setMonth(monthNames[currentMonthIndex]);
              }

              setYear(newYear);
            }}
          />
          <div className={styles.search}>
            <img src={Search} onClick={() => setIsOpen(true)}></img>
          </div>
        </div>

        <ReportHeader header={t(header)} reportType={reportType} />
        {!showCheckedIn ? (
          <div className={styles.row}>
            <div className={styles.attendees}>
              <label>{reportType == "major" ? t("Attendees") : t("Type")}</label>
              <SelectDropDown
                options={reportType == "major" ? Attendees : Types}
                placeholder={reportType == "major" ? attendees : type}
                onChange={(newOption) =>
                  reportType == "major"
                    ? setAttendeees(newOption)
                    : setType(newOption)
                }
                fromReport={true}
              />
            </div>
            <div className={styles.locations}>
              <label>{reportType == "major" ? t("Locations") : t("Status")}</label>
              <SelectDropDown
                options={reportType == "major" ? Locations : Statuses}
                placeholder={reportType == "major" ? location : status}
                onChange={(newOption) =>
                  reportType == "major"
                    ? setLocation(newOption)
                    : setStatus(newOption)
                }
                fromReport={true}
              />
            </div>
          </div>
        ) : (
          <div className={styles.checkedInDivider} />
        )}

        <div className={styles.width}>
          <ReportItem
            name={reportData.iboInfo.iboName}
            number={reportData.iboInfo.total.value}
            trend={reportData.iboInfo.total.trend}
            color={"#415364"}
            onClick={
              !showCheckedIn
                ? () => {
                    if (reportType === "membership") {
                      const context = {
                        reportType,
                        ibo: reportData.iboInfo.ibo,
                        year,
                        month,
                        header,
                        attendees,
                        location,
                        type,
                        status,
                      };
                      const stack =
                        (history.location.state &&
                          history.location.state.reportStack) ||
                        [];
                      history.push("/reports-info", {
                        iboNum: reportData.iboInfo.ibo,
                        fromReports: true,
                        reportStack: [...stack, context],
                      });
                    } else {
                      setShowCheckedIn(reportData.iboInfo.ibo);
                    }
                  }
                : null
            }
          />
        </div>

        <ReportInfoItem
          reportInfo={reportData.iboInfo}
          reportType={reportType}
        />

        {showCheckedIn ? (
          <>
            <EventHeader location="Las Vegas, NV" date="April 13-14, 2025" />
            <CheckedInList
              checkedIns={[
                { date: "2024-10-21", time: "09:30 AM" },
                { date: "2024-10-22", time: "10:00 AM" },
                { date: "2024-10-23", time: "11:15 AM" },
              ]}
            />
          </>
        ) : (
          <>
            {reportData.downline.filter((item) => item.pinLevel == 4)?.length >
              0 && (
              <>
                <ReportList
                  header={"Diamond(s)"}
                  items={reportData.downline.filter(
                    (item) => item.pinLevel == 4
                  )}
                  onClick={(ibo) => changeIbo(ibo)}
                />
                <hr className={styles.divider} />
              </>
            )}
            {reportData.downline.filter((item) => item.pinLevel == 3)?.length >
              0 && (
              <>
                <ReportList
                  header={"Emerald(s)"}
                  items={reportData.downline.filter(
                    (item) => item.pinLevel == 3
                  )}
                  onClick={(ibo) => changeIbo(ibo)}
                />
                <hr className={styles.divider} />
              </>
            )}
            {reportData.downline.filter((item) => item.pinLevel == 2)?.length >
              0 && (
              <>
                <ReportList
                  header={"Platinum(s)"}
                  items={reportData.downline.filter(
                    (item) => item.pinLevel == 2
                  )}
                  onClick={(ibo) => changeIbo(ibo)}
                />
                <div className={styles.divider} />
              </>
            )}
            {reportData.downline.filter((item) => item.pinLevel == 1)?.length >
              0 && (
              <>
                <ReportList
                  header={"IBO(s)"}
                  items={reportData.downline.filter(
                    (item) => item.pinLevel == 1
                  )}
                  onClick={(ibo) => {
                    if (reportType === "membership") {
                      const context = {
                        reportType,
                        ibo: ibo.ibo,
                        year,
                        month,
                        header,
                        attendees,
                        location,
                        type,
                        status,
                      };
                      const stack =
                        (history.location.state &&
                          history.location.state.reportStack) ||
                        [];
                      history.push("/reports-info", {
                        iboNum: ibo.ibo,
                        fromReports: true,
                        reportStack: [...stack, context],
                      });
                    } else {
                      setShowCheckedIn(ibo.ibo);
                    }
                  }}
                />
                <hr className={styles.divider} />
              </>
            )}
          </>
        )}
      </div>
    </>
  );
};
